/**
 * The builder's tile list: pick a tile to edit, add one, remove one, move it
 * up or down. The order here is the order (and packing) on the dashboard.
 *
 * @module components/looker/admin/builder/BuilderTileList
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SpecTile } from '@/services/looker/lookerTemplateSpec';

interface Props {
  tiles: readonly SpecTile[];
  selectedKey: string | null;
  /** Tile indexes with a validation problem, marked in the list. */
  invalid: ReadonlySet<number>;
  canAdd: boolean;
  onSelect: (key: string) => void;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onMove: (from: number, to: number) => void;
}

const SMALL =
  'rounded-md border border-neutral-300 bg-white px-1.5 py-0.5 text-xs text-ink-900 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-40';

export const BuilderTileList: React.FC<Props> = ({ tiles, selectedKey, invalid, canAdd, onSelect, onAdd, onRemove, onMove }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-ink-900">{t('looker.builder.tiles')}</h2>
      {tiles.length === 0 && <p className="text-sm text-neutral-800">{t('looker.builder.noTiles')}</p>}
      <ol className="space-y-1">
        {tiles.map((tile, i) => {
          const name = tile.title.trim() || t('looker.builder.untitled');
          const selected = tile.key === selectedKey;
          return (
            <li
              key={tile.key}
              className={`flex items-center gap-1 rounded-md border px-2 py-1 ${selected ? 'border-ink-900 bg-neutral-50' : 'border-neutral-200'}`}
            >
              <button
                type="button"
                className="min-w-0 flex-1 truncate text-left text-sm text-ink-900"
                aria-pressed={selected}
                onClick={() => onSelect(tile.key)}
              >
                {invalid.has(i) && <span className="mr-1 font-bold text-red-700" aria-hidden>!</span>}
                {name}
              </button>
              <button type="button" className={SMALL} disabled={i === 0}
                aria-label={t('looker.builder.moveUp', { title: name })} onClick={() => onMove(i, i - 1)}>↑</button>
              <button type="button" className={SMALL} disabled={i === tiles.length - 1}
                aria-label={t('looker.builder.moveDown', { title: name })} onClick={() => onMove(i, i + 1)}>↓</button>
              <button type="button" className={SMALL}
                aria-label={t('looker.builder.removeTile', { title: name })} onClick={() => onRemove(i)}>×</button>
            </li>
          );
        })}
      </ol>
      <button type="button" className={`${SMALL} px-2 py-1 text-sm`} disabled={!canAdd} onClick={onAdd}>
        {t('looker.builder.addTile')}
      </button>
    </div>
  );
};
